import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowRight, Home } from "lucide-react";
import { lookupRoute } from "@/lib/routeMap";

const HIDE_ON = new Set(["/", "/login", "/register"]);

/**
 * PageFooterNav — renders on every page (just above the global Footer).
 *
 * Two pieces:
 *  - Top row (breadcrumb): "Home · Parent · Current" — works as quick navigation.
 *  - Card row: "Up next" suggestion (+ optional secondary). Helps the user
 *    discover the natural next page instead of dead-ending.
 *
 * The component reads /app/frontend/src/lib/routeMap.js for route metadata.
 * If a route has no entry, only the "Home" link is rendered as a safe fallback.
 */
export default function PageFooterNav() {
  const { pathname } = useLocation();

  // Hide on the homepage (the homepage *is* the home) and auth flows that
  // already have their own intra-flow navigation.
  if (HIDE_ON.has(pathname)) return null;

  const meta = lookupRoute(pathname);
  const next = meta?.next;
  const secondary = meta?.secondary;
  // Don't render a "parent" breadcrumb when parent is just Home — we already show Home as the first crumb.
  const parent = meta?.parent && meta.parent.path !== "/" ? meta.parent : null;

  return (
    <nav className="border-t border-border/60 bg-secondary/30" aria-label="Page navigation" data-testid="page-footer-nav">
      <div className="container-x py-10 md:py-14">
        {/* Breadcrumb row */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted-foreground" data-testid="page-breadcrumb">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-foreground transition-colors" data-testid="breadcrumb-home">
            <Home size={11} /> Home
          </Link>
          {parent && (
            <>
              <span aria-hidden>/</span>
              <Link to={parent.path} className="hover:text-foreground transition-colors" data-testid="breadcrumb-parent">{parent.title}</Link>
            </>
          )}
          {meta?.title && (
            <>
              <span aria-hidden>/</span>
              <span className="text-foreground/80">{meta.title}</span>
            </>
          )}
        </div>

        {/* Up next + secondary cards */}
        {(next || secondary) && (
          <div className="mt-6 grid md:grid-cols-2 gap-3" data-testid="page-up-next">
            {next && (
              <Link to={next.path} className="card-surface group flex items-center justify-between" data-testid="up-next-primary">
                <div>
                  <p className="eyebrow">Up next</p>
                  <p className="mt-2 font-display text-lg md:text-xl tracking-tight">{next.title}</p>
                </div>
                <ArrowRight size={18} className="text-muted-foreground group-hover:text-signal-lime transition-colors" />
              </Link>
            )}
            {secondary && (
              <Link to={secondary.path} className="card-surface group flex items-center justify-between" data-testid="up-next-secondary">
                <div>
                  <p className="eyebrow">Or try</p>
                  <p className="mt-2 font-display text-lg md:text-xl tracking-tight">{secondary.title}</p>
                </div>
                <ArrowRight size={18} className="text-muted-foreground group-hover:text-signal-lime transition-colors" />
              </Link>
            )}
          </div>
        )}

        {/* Always-on back-to-home fallback for pages with no map entry */}
        {!meta && (
          <div className="mt-4">
            <Link to="/" className="btn-secondary inline-flex !py-2 !px-3 text-xs" data-testid="page-back-home">
              <ArrowLeft size={12} /> Back to home
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}
